import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';
import { Target, Wallet, Briefcase, ChartColumn, Bike, ShoppingCart, Banknote, Ticket, ArrowDownLeft, ArrowUpRight } from 'lucide-react';

const fmt = v => v !== undefined && v !== null ? 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : 'R$ 0,00';
const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const card = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '24px' };
const cardTitle = { fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' };
const kpiBox = { background: '#F4F6F9', borderRadius: '8px', padding: '12px 14px' };
// KPIs do topo: fundo escuro (mesma cor da sidebar), texto branco
// azul-marinho um tom mais claro que a sidebar (#0B1628), para não parecer colado nela
const kpiCard = { ...card, background: '#1A2332', border: 'none', padding: '20px 18px' };
const kpiTitle = { ...cardTitle, color: 'rgba(255,255,255,0.6)' };
const kpiBoxDark = { background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px 14px' };
const kpiValDark = (size) => ({ ...kpiVal(null, size), color: '#fff', whiteSpace: 'nowrap' });
const kpiSub = { fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const kpiBoxRow = { ...kpiBoxDark, display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 10px' };
const kpiIcone = (Icone, cor) => (
  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: cor + '2E', color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icone size={18} strokeWidth={2.2} />
  </div>
);
const tituloIcone = (Icone, texto) => <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Icone size={14} strokeWidth={2.2} />{texto}</span>;
const th = { padding: '10px 12px', background: '#F5F7FA', color: '#8A94A3', fontSize: '10.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'left' };
const td = { padding: '14px 12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };
const badge = (c) => ({ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '500', whiteSpace: 'nowrap', background: c === 'info' ? '#E6F1FB' : c === 'warn' ? '#FFF3E0' : c === 'danger' ? '#FDECEA' : '#EAF5EE', color: c === 'info' ? '#185FA5' : c === 'warn' ? '#B06000' : c === 'danger' ? '#C0392B' : '#2D7D4F' });
const kpiVal = (c, size) => ({ fontSize: size || '24px', fontWeight: '700', letterSpacing: '-0.5px', fontFamily: "'DM Mono',monospace,sans-serif", color: c === 'green' ? '#2D7D4F' : c === 'blue' ? '#1E5FA5' : c === 'red' ? '#C0392B' : c === 'orange' ? '#B06000' : '#1C2533' });
const diasBadge = (d) => { const n = parseInt(d || 0); return { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: n < 20 ? '#EAF5EE' : n < 35 ? '#FFF3E0' : '#FDECEA', color: n < 20 ? '#2D7D4F' : n < 35 ? '#B06000' : '#C0392B' }; };
const variacaoBadge = (atual, anterior) => {
  if (!anterior || parseFloat(anterior) === 0) return null;
  const pct = ((parseFloat(atual) - parseFloat(anterior)) / parseFloat(anterior) * 100).toFixed(0);
  const up = pct >= 0;
  return <span style={{ fontSize: '11px', fontWeight: '600', color: up ? '#2D7D4F' : '#C0392B', background: up ? '#EAF5EE' : '#FDECEA', padding: '2px 6px', borderRadius: '6px', whiteSpace: 'nowrap' }}>{up ? '▲' : '▼'} {Math.abs(pct)}%</span>;
};

// Arredondamento igual ao backend (maior resto) — para o preview do modal
const calcMetasPreview = (mt, a, b, c) => {
  const cats = [{ k: 'e', x: mt * a / 100 }, { k: 'c1', x: mt * b / 100 }, { k: 'c2', x: mt * c / 100 }]
    .map(o => ({ ...o, piso: Math.floor(o.x), r: o.x - Math.floor(o.x) }));
  let sobra = mt - cats.reduce((s, o) => s + o.piso, 0);
  const ord = [...cats].sort((x, y) => y.r - x.r);
  const win = new Set();
  for (let i = 0; i < sobra && i < ord.length; i++) win.add(ord[i].k);
  const out = {};
  cats.forEach(o => { out[o.k] = o.piso + (win.has(o.k) ? 1 : 0); });
  return out;
};

// Medidor semicircular da meta total (visual; recebe o % já calculado)
function GaugeMeta({ pct, cor }) {
  const r = 44, c = Math.PI * r, v = Math.max(0, Math.min(100, pct || 0));
  return (
    <svg width="112" height="64" viewBox="0 0 112 64" aria-label={`${v}% da meta`}>
      <path d="M 12 58 A 44 44 0 0 1 100 58" fill="none" stroke="#EEF1F5" strokeWidth="11" strokeLinecap="round" />
      <path d="M 12 58 A 44 44 0 0 1 100 58" fill="none" stroke={cor} strokeWidth="11" strokeLinecap="round"
        strokeDasharray={`${(v / 100) * c} ${c}`} style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      <text x="56" y="54" textAnchor="middle" style={{ fontSize: '19px', fontWeight: 700, fontFamily: "'DM Mono',monospace", fill: cor }}>{v}%</text>
    </svg>
  );
}

// Barra de progresso de meta por categoria (Dashboard)
function MetaBarD({ label, vendido, meta, corBase = '#1A6DFF' }) {
  const pct = meta > 0 ? Math.min(100, Math.round((vendido / meta) * 100)) : (vendido > 0 ? 100 : 0);
  const bateu = meta > 0 && vendido >= meta;
  const cor = bateu ? '#2D7D4F' : corBase;
  return (
    <div title={`${vendido} vendidas / ${meta} meta (${pct}%)`} style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6B7A8D', marginBottom: '5px' }}>
        <span>{label}</span>
        <span style={{ fontWeight: '600', color: bateu ? '#2D7D4F' : '#1C2533', whiteSpace: 'nowrap', marginLeft: '8px' }}>{vendido}/{meta} <span style={{ color: '#9AA3AE' }}>({pct}%)</span></span>
      </div>
      <div style={{ height: '10px', background: '#EEF1F5', borderRadius: '6px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: cor, borderRadius: '6px', transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

// Modal master: define meta total + distribuição em %
function MetaModal({ inicial, mes, ano, onClose, onSaved }) {
  const [metaTotal, setMetaTotal] = useState(String(inicial.meta_total));
  const [pE, setPE] = useState(Number(inicial.perc_eletricas));
  const [p1, setP1] = useState(Number(inicial.perc_125_190));
  const [p2, setP2] = useState(Number(inicial.perc_200_plus));
  const [salvando, setSalvando] = useState(false);

  const total = parseInt(metaTotal, 10) || 0;
  const soma = Math.round((Number(pE) + Number(p1) + Number(p2)) * 100) / 100;
  const somaOk = Math.abs(soma - 100) <= 0.01;
  const podeSalvar = somaOk && total > 0 && !salvando;
  const prev = calcMetasPreview(total, Number(pE) || 0, Number(p1) || 0, Number(p2) || 0);

  const salvar = async () => {
    if (!podeSalvar) return;
    setSalvando(true);
    try {
      await api.put('/metas', { mes, ano, meta_total: total, perc_eletricas: Number(pE), perc_125_190: Number(p1), perc_200_plus: Number(p2) });
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Erro ao salvar meta'); }
    finally { setSalvando(false); }
  };

  const linhaSlider = (label, val, set, cor) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <span style={{ fontSize: '12.5px', color: '#3C4655', fontWeight: '500' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <input type="number" min="0" max="100" value={val}
            onChange={e => set(e.target.value === '' ? 0 : Math.max(0, Math.min(100, Number(e.target.value))))}
            style={{ width: '56px', padding: '4px 6px', textAlign: 'right', border: '0.5px solid rgba(26,58,92,0.2)', borderRadius: '6px', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }} />
          <span style={{ fontSize: '12px', color: '#9AA3AE' }}>%</span>
        </div>
      </div>
      <input type="range" min="0" max="100" step="1" value={val} onChange={e => set(Number(e.target.value))}
        style={{ width: '100%', accentColor: cor }} />
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', padding: '24px', width: '100%', maxWidth: '440px', fontFamily: "'DM Sans',system-ui,sans-serif", boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: '17px', fontWeight: '700', color: '#1C2533', marginBottom: '2px' }}>Editar meta do mês</div>
        <div style={{ fontSize: '12px', color: '#9AA3AE', marginBottom: '18px' }}>Meta por vendedor · aplicada a todos</div>

        {/* Meta total */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ fontSize: '12.5px', color: '#3C4655', fontWeight: '600', display: 'block', marginBottom: '5px' }}>Meta total do mês (motos)</label>
          <input type="number" min="1" value={metaTotal} onChange={e => setMetaTotal(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', border: '0.5px solid rgba(26,58,92,0.2)', borderRadius: '8px', fontSize: '15px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' }} />
        </div>

        {/* Distribuição % */}
        <div style={{ fontSize: '11px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Distribuição por categoria</div>
        {linhaSlider('Elétricas', pE, setPE, '#1E5FA5')}
        {linhaSlider('Convencionais 100–190cc', p1, setP1, '#B06000')}
        {linhaSlider('Acima de 200cc', p2, setP2, '#2D7D4F')}

        {/* Indicador da soma */}
        <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: '700', padding: '7px', borderRadius: '8px', marginBottom: '12px',
          background: somaOk ? '#EAF5EE' : '#FFF3E0', color: somaOk ? '#2D7D4F' : '#B06000' }}>
          {somaOk ? '✓ 100%' : `⚠ ${soma}% (${soma < 100 ? `faltam ${(100 - soma).toFixed(0)}%` : `retire ${(soma - 100).toFixed(0)}%`})`}
        </div>

        {/* Preview calculado */}
        <div style={{ background: '#F4F6F9', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#1C2533', marginBottom: '18px', textAlign: 'center' }}>
          = <b>{prev.e}</b> elétricas + <b>{prev.c1}</b> convencionais + <b>{prev.c2}</b> acima de 200cc
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 16px', background: 'transparent', border: '0.5px solid rgba(26,58,92,0.25)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
          <button onClick={salvar} disabled={!podeSalvar}
            style={{ padding: '9px 20px', background: podeSalvar ? '#1A3A5C' : '#B4BCC6', border: 'none', color: '#fff', borderRadius: '8px', cursor: podeSalvar ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [dados, setDados] = useState(null);
  const [metaProg, setMetaProg] = useState(null);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const somenteLeitura = isVisualizacao(usuario); // visualização vê como admin, sem ações
  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente' || somenteLeitura;
  // Card de Financeiro só nos planos pro e master (mapeia nomes legados)
  const PLANO_NORM = { basico: 'starter', profissional: 'pro', completo: 'master' };
  const planoNorm = PLANO_NORM[usuario?.plano] || usuario?.plano || 'starter';
  const temFinanceiro = planoNorm === 'pro' || planoNorm === 'master';

  const carregar = () => {
    api.get('/relatorios/dashboard').then(r => setDados(r.data)).catch(console.error);
    api.get('/metas/progresso').then(r => setMetaProg(r.data)).catch(console.error);
  };

  useEffect(() => { carregar(); }, []);

  if (!dados) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9AA3AE', fontSize: '14px' }}>Carregando...</div>
  );

  const mesAtual = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  // Novo sistema: metas por categoria (metaProg pode estar carregando).
  const mp = metaProg;
  const metaTotalCfg = mp?.meta_total || 0;
  const totalVendido = mp?.total_vendido || 0;
  const pctMeta = metaTotalCfg > 0 ? Math.min(Math.round((totalVendido / metaTotalCfg) * 100), 100) : 0;
  const corMeta = pctMeta >= 100 ? '#2D7D4F' : pctMeta >= 60 ? '#B06000' : '#C0392B';

  // Card reutilizável de metas por categoria (Dashboard vendedor e admin)
  const cardMetasCategoria = !mp ? (
    <div style={{ fontSize: '12px', color: '#9AA3AE', padding: '8px 0' }}>Carregando metas…</div>
  ) : (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '26px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: corMeta }}>{totalVendido}</span>
          <span style={{ fontSize: '14px', color: '#9AA3AE', marginLeft: '4px' }}>/ {metaTotalCfg} motos</span>
        </div>
        <GaugeMeta pct={pctMeta} cor={corMeta} />
      </div>
      <MetaBarD label="Elétricas" vendido={mp.vendido_eletricas} meta={mp.meta_eletricas} corBase="#1A6DFF" />
      <MetaBarD label="Convencionais 100–190cc" vendido={mp.vendido_125_190} meta={mp.meta_125_190} corBase="#7C5CFF" />
      <MetaBarD label="Acima de 200cc" vendido={mp.vendido_200_plus} meta={mp.meta_200_plus} corBase="#F39C12" />
    </>
  );

  // ===================== DASHBOARD VENDEDOR =====================
  if (!isAdmin) {
    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>Olá, {usuario?.nome?.split(' ')[0]} 👋</div>
          <div style={{ fontSize: '13px', color: '#6B7A8D', marginTop: '2px' }}>{usuario?.empresa_nome || 'Minha Loja'} · {mesAtual}</div>
        </div>

        {/* Meta do mês por categoria — vendedor */}
        <div style={{ ...card, borderLeft: `3px solid ${corMeta}`, marginBottom: '20px' }}>
          <div style={cardTitle}>{tituloIcone(Target, 'Sua meta do mês')}</div>
          {cardMetasCategoria}
          {mp && (
            <div style={{ fontSize: '13px', color: '#6B7A8D', marginTop: '6px' }}>
              {metaTotalCfg - totalVendido > 0 ? `Faltam ${metaTotalCfg - totalVendido} motos para a meta total` : '🎉 Meta total atingida!'}
            </div>
          )}
        </div>

        {/* Ranking vendedores */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={card}>
            <div style={cardTitle}>
              {tituloIcone(ChartColumn, 'Ranking vendedores')}
              <span style={{ ...badge('info'), fontWeight: '600', letterSpacing: '0.4px' }}>MÊS</span>
            </div>
            {dados.ranking.length === 0 ? (
              <div style={{ color: '#9AA3AE', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>Sem vendas no mês</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {dados.ranking.map((v, i) => {
                  const maxV = dados.ranking[0]?.total || 1;
                  const pct = Math.round((v.total / maxV) * 100);
                  const cores = ['#1A6DFF', '#F39C12', '#9AA3AE', '#9AA3AE', '#9AA3AE'];
                  const bgs = ['#E8F0FF', '#FFF3E0', '#F4F6F9', '#F4F6F9', '#F4F6F9'];
                  const isMe = v.nome === usuario?.nome;
                  return (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: isMe ? '8px 10px' : '0', background: isMe ? '#E6F1FB' : 'transparent', borderRadius: isMe ? '8px' : '0' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: bgs[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: cores[i], flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: isMe ? '700' : '500', color: isMe ? '#185FA5' : '#1C2533' }}>{v.nome} {isMe && '(você)'}</div>
                        <div style={{ height: '6px', borderRadius: '3px', background: '#F0F3F7', marginTop: '5px' }}>
                          <div style={{ height: '100%', borderRadius: '3px', background: isMe ? '#185FA5' : cores[i], width: pct + '%' }}></div>
                        </div>
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#1C2533' }}>{v.total}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Estoque disponível */}
          <div style={card}>
            <div style={cardTitle}>
              <span>Estoque disponível</span>
              <button onClick={() => navigate('/estoque')} style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '500' }}>Ver tudo →</button>
            </div>
            <div style={kpiBox}>
              <div style={kpiVal('orange')}>{dados.estoque.total}</div>
              <div style={{ fontSize: '12px', color: '#9AA3AE', marginTop: '4px' }}>motos para vender</div>
            </div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {dados.mais_parados.slice(0, 4).map(v => (
                <div key={v.id} onClick={() => navigate('/estoque/' + v.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: '#F4F6F9', borderRadius: '7px', cursor: 'pointer' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#1C2533' }}>{v.marca} {v.modelo} {v.ano_mod}</div>
                  <span style={diasBadge(v.dias)}>{parseInt(v.dias)}d</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===================== DASHBOARD ADMIN =====================
  return (
    <div>
      {showMetaModal && mp && (
        <MetaModal
          inicial={{ meta_total: mp.meta_total, perc_eletricas: mp.perc_eletricas, perc_125_190: mp.perc_125_190, perc_200_plus: mp.perc_200_plus }}
          mes={mp.mes} ano={mp.ano}
          onClose={() => setShowMetaModal(false)}
          onSaved={() => { setShowMetaModal(false); carregar(); }}
        />
      )}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1C2533', letterSpacing: '-0.3px' }}>Dashboard</div>
        <div style={{ fontSize: '13px', color: '#6B7A8D', marginTop: '2px' }}>{usuario?.empresa_nome || 'Minha Loja'} · {mesAtual}</div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '20px', marginBottom: '20px' }}>
        <div style={kpiCard}>
          <div style={kpiTitle}><span>Motos em estoque</span>{dados.estoque.parados > 0 && <span style={badge('warn')}>{dados.estoque.parados} +30d</span>}</div>
          <div style={kpiBoxRow}>{kpiIcone(Bike, '#F39C12')}<div style={{ minWidth: 0 }}><div style={kpiValDark()}>{dados.estoque.total}</div><div style={kpiSub}>unidades · {fmt(dados.estoque.valor_total)}</div></div></div>
        </div>
        <div style={kpiCard}>
          <div style={kpiTitle}><span>Vendas no mês</span>{variacaoBadge(dados.vendas_mes.total, dados.vendas_mes_ant.total)}</div>
          <div style={kpiBoxRow}>{kpiIcone(ShoppingCart, '#4D8DFF')}<div style={{ minWidth: 0 }}><div style={kpiValDark()}>{dados.vendas_mes.total}</div><div style={kpiSub}>mês anterior: {dados.vendas_mes_ant.total}</div></div></div>
        </div>
        <div style={kpiCard}>
          <div style={kpiTitle}><span>Receita do mês</span>{variacaoBadge(dados.vendas_mes.receita, dados.vendas_mes_ant.receita)}</div>
          <div style={kpiBoxRow}>{kpiIcone(Banknote, '#34C77B')}<div style={{ minWidth: 0 }}><div style={kpiValDark('16px')}>{fmt(dados.vendas_mes.receita)}</div><div style={kpiSub}>lucro: {fmt(dados.lucro_mes)}</div></div></div>
        </div>
        <div style={kpiCard}>
          <div style={kpiTitle}><span>Ticket médio</span></div>
          <div style={kpiBoxRow}>{kpiIcone(Ticket, '#A78BFA')}<div style={{ minWidth: 0 }}><div style={kpiValDark('16px')}>{fmt(dados.ticket_medio)}</div><div style={kpiSub}>por venda no mês</div></div></div>
        </div>
      </div>

      {/* Meta + Financeiro + Capital + Ranking */}
      <div style={{ display: 'grid', gridTemplateColumns: temFinanceiro ? '1.2fr 1fr 1fr 1fr' : '1.2fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={{ ...card, borderLeft: `3px solid ${corMeta}` }}>
          <div style={cardTitle}>
            {tituloIcone(Target, 'Meta do mês')}
            {!somenteLeitura && <button onClick={() => setShowMetaModal(true)}
              style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>Editar</button>}
          </div>
          {cardMetasCategoria}
          {mp && (
            <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '4px' }}>
              {metaTotalCfg - totalVendido > 0 ? `Faltam ${metaTotalCfg - totalVendido} motos` : '🎉 Meta atingida!'}
            </div>
          )}
        </div>

        {temFinanceiro && (
        <div style={card}>
          <div style={cardTitle}>{tituloIcone(Wallet, 'Financeiro')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ ...kpiBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '12px 12px' }}><span style={{ fontSize: '12px', color: '#6B7A8D', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}><span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#EAF5EE', color: '#2D7D4F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ArrowDownLeft size={14} strokeWidth={2.5} /></span>Receitas (mês)</span><span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#2D7D4F', whiteSpace: 'nowrap' }}>{fmt(dados.receita_mes)}</span></div>
            <div style={{ ...kpiBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '12px 12px' }}><span style={{ fontSize: '12px', color: '#6B7A8D', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}><span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#FDECEA', color: '#E74C3C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ArrowUpRight size={14} strokeWidth={2.5} /></span>Despesas (mês)</span><span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#C0392B', whiteSpace: 'nowrap' }}>{fmt(dados.despesa_mes)}</span></div>
            <div style={{ ...kpiBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0B1628', padding: '16px 12px', gap: '8px' }}><span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: '600' }}>Resultado do mês</span><span style={{ fontFamily: "'DM Mono',monospace", fontSize: '15px', fontWeight: '700', color: parseFloat(dados.resultado_mes) < 0 ? '#FF8A80' : '#fff', whiteSpace: 'nowrap' }}>{fmt(dados.resultado_mes)}</span></div>
            <div style={{ fontSize: '11px', color: '#9AA3AE', display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '0 2px' }}><span>Próx. 30d: a receber <b style={{ color: '#2D7D4F' }}>{fmt(dados.a_receber.total)}</b></span><span>a pagar <b style={{ color: '#C0392B' }}>{fmt(dados.a_pagar.total)}</b></span></div>
            {dados.vencidos_receber && dados.vencidos_pagar && (
              <div style={{ fontSize: '11px', color: '#9AA3AE', display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '0 2px' }}><span>Vencidos: a receber <b style={{ color: '#E67E22' }}>{fmt(dados.vencidos_receber.total)}</b></span><span>a pagar <b style={{ color: '#E67E22' }}>{fmt(dados.vencidos_pagar.total)}</b></span></div>
            )}
          </div>
        </div>
        )}

        <div style={card}>
          <div style={cardTitle}>{tituloIcone(Briefcase, 'Capital imobilizado')}</div>
          <div><div style={{ ...kpiVal('blue'), fontSize: '21px', whiteSpace: 'nowrap' }}>{fmt(dados.estoque.valor_total)}</div><div style={{ fontSize: '12px', color: '#6B7A8D', marginTop: '4px' }}>em {dados.estoque.total} veículos</div></div>
          <div style={{ marginTop: '16px', height: '0.5px', background: 'rgba(26,58,92,0.1)' }}></div>
          <div style={{ marginTop: '14px' }}><div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '4px' }}>Custo médio/unidade</div><div style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#1C2533' }}>{dados.estoque.total > 0 ? fmt(parseFloat(dados.estoque.custo_total) / parseInt(dados.estoque.total)) : 'R$ 0,00'}</div></div>
        </div>

        <div style={card}>
          <div style={cardTitle}>{tituloIcone(ChartColumn, 'Ranking vendedores')}<span style={{ ...badge('info'), fontWeight: '600', letterSpacing: '0.4px' }}>MÊS</span></div>
          {dados.ranking.length === 0 ? <div style={{ color: '#9AA3AE', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>Sem vendas</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {dados.ranking.map((v, i) => {
                const maxV = dados.ranking[0]?.total || 1;
                const pct = Math.round((v.total / maxV) * 100);
                const cores = ['#1A6DFF', '#F39C12', '#9AA3AE', '#9AA3AE', '#9AA3AE'];
                const bgs = ['#E8F0FF', '#FFF3E0', '#F4F6F9', '#F4F6F9', '#F4F6F9'];
                return (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: bgs[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: cores[i], flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', color: '#1C2533' }}>{v.nome}</div>
                      <div style={{ height: '6px', borderRadius: '3px', background: '#F0F3F7', marginTop: '5px' }}><div style={{ height: '100%', borderRadius: '3px', background: cores[i], width: pct + '%' }}></div></div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#1C2533' }}>{v.total}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tabelas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div style={card}>
          <div style={cardTitle}><span>Estoque — mais tempo parado</span><button onClick={() => navigate('/estoque')} style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '500' }}>Ver tudo →</button></div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Veículo', 'Venda', 'Dias'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {dados.mais_parados.length === 0 && <tr><td colSpan={3} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '20px 0' }}>Sem estoque</td></tr>}
              {dados.mais_parados.map(v => (
                <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/estoque/' + v.id)}>
                  <td style={td}><div style={{ fontWeight: '500', fontSize: '13px' }}>{v.marca} {v.modelo}</div><div style={{ fontSize: '11px', color: '#9AA3AE' }}>{v.ano_mod}</div></td>
                  <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#2D7D4F', fontWeight: '600', whiteSpace: 'nowrap' }}>{fmt(v.valor_venda)}</td>
                  <td style={td}><span style={diasBadge(v.dias)}>{parseInt(v.dias)}d</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={card}>
          <div style={cardTitle}><span>Últimas vendas</span><button onClick={() => navigate('/vendas')} style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '500' }}>Ver tudo →</button></div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Data', 'Veículo', 'Cliente', 'Valor'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {dados.ultimas_vendas.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '20px 0' }}>Sem vendas</td></tr>}
              {dados.ultimas_vendas.map((v, i) => (
                <tr key={i}>
                  <td style={{ ...td, color: '#6B7A8D', fontSize: '11px', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap' }}>{fmtData(v.data_venda)}</td>
                  <td style={{ ...td, fontWeight: '500', fontSize: '12px' }}>{v.marca} {v.modelo}</td>
                  <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{v.cliente_nome}</td>
                  <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#2D7D4F', fontWeight: '600', whiteSpace: 'nowrap' }}>{fmt(v.valor_final)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
